import React from 'react';
import { Link as RRLink } from 'react-router-dom';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import { Link } from '../Link';
import BreadcrumbChevron from './BreadcrumbChevron';
import CollapsedBreadcrumbs from './CollapsedBreadcrumbs';

export interface BreadcrumbItem {
  /** The URL that this Breadcrumbs should navigate to when clicked */
  href: string;
  /** The React node that will be displayed on this particular Breadcrumb
   * children can be: numbers, strings, elements or an array (or fragment) containing these types.
   */
  children: React.ReactNode;
}

export interface BreadcrumbProps {
  /** A list of `BreadcrumbsItem` objects ( `{href,children}` ) that will construct the Breadcrumb */
  items: BreadcrumbItem[];
  /** Truncates component if breadcrumb list is too long.
   * At least 4 breadcrumbs must exist for truncation to occur.
   */
  truncated?: boolean;
}

/** Breadcrumb is a way to navigate back to where you came from within the app */
export const Breadcrumbs: React.FC<BreadcrumbProps> = ({ items, truncated = false }) => {
  const shouldTruncate = truncated && items.length > 3;

  // Manually handle truncation
  if (shouldTruncate) {
    // When truncated: show first item, collapsed popover, last 2 items
    const firstItem = items[0];
    const collapsedItems = items.slice(1, -2);
    const lastTwoItems = items.slice(-2);

    return (
      <MuiBreadcrumbs aria-label="Breadcrumbs" separator={<BreadcrumbChevron />}>
        {/* First item */}
        <Link
          key={firstItem.href}
          as={RRLink}
          to={firstItem.href}
          variant="neutral"
          truncated
          maxWidth="450px"
          fontSize="medium"
        >
          {firstItem.children}
        </Link>

        {/* Collapsed items in popover */}
        <CollapsedBreadcrumbs crumbs={collapsedItems} />

        {/* Last 2 items */}
        {lastTwoItems.map((item, index) => {
          const isLastBreadcrumb = index === lastTwoItems.length - 1;
          return (
            <Link
              key={item.href}
              as={RRLink}
              to={item.href}
              variant="neutral"
              truncated
              maxWidth="450px"
              aria-current={isLastBreadcrumb ? 'page' : undefined}
              data-active={isLastBreadcrumb ? true : undefined}
              fontSize="medium"
            >
              {item.children}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    );
  }

  return (
    <MuiBreadcrumbs aria-label="Breadcrumbs" separator={<BreadcrumbChevron />}>
      {items.map((item, index) => {
        const isLastBreadcrumb = index === items.length - 1;
        return (
          <Link
            key={item.href}
            as={RRLink}
            to={item.href}
            variant="neutral"
            truncated
            maxWidth="450px"
            aria-current={isLastBreadcrumb ? 'page' : undefined}
            data-active={isLastBreadcrumb ? true : undefined}
            fontSize="medium"
          >
            {item.children}
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
};
